import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Bouton, Etiquette, Liste } from '../components/ui/Base'
import { EtatVide, MessageErreur } from '../components/ui/Etats'
import { SqueletteTableau } from '../components/ui/Squelette'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { EntreeAudit, Page } from '../lib/api/types'
import { formatHorodatage } from '../lib/formatters'

const TAILLE = 25

const TONS: Record<string, 'neutre' | 'danger' | 'info' | 'attention'> = {
  LECTURE: 'neutre',
  CONNEXION: 'info',
  ECHEC_CONNEXION: 'danger',
  MODIFICATION: 'attention',
  CREATION: 'info',
  SUPPRESSION: 'danger',
  EXPORT: 'attention',
}

/**
 * Journal d'audit — lecture seule, `TITULAIRE` uniquement (R6).
 *
 * Aucune écriture, aucune suppression : un journal qu'on peut modifier depuis
 * l'interface ne prouve plus rien.
 */
export function JournalAudit() {
  const [parametres, setParametres] = useSearchParams()
  const [depliees, setDepliees] = useState<Set<string>>(new Set())

  const page = Number(parametres.get('page') ?? 1)
  const action = parametres.get('action') ?? ''
  const entite = parametres.get('entite') ?? ''

  const requeteParams = { page, taille: TAILLE, ...(action ? { action } : {}), ...(entite ? { entite } : {}) }

  const requete = useQuery({
    queryKey: cles.journalAudit.liste(requeteParams),
    queryFn: ({ signal }) => {
      const url = new URLSearchParams()
      Object.entries(requeteParams).forEach(([c, v]) => url.set(c, String(v)))
      return api.get<Page<EntreeAudit>>(`/journal-audit?${url.toString()}`, signal)
    },
  })

  function changer(cle: string, valeur: string) {
    const suivants = new URLSearchParams(parametres)
    if (valeur) suivants.set(cle, valeur)
    else suivants.delete(cle)
    if (cle !== 'page') suivants.delete('page')
    setParametres(suivants, { replace: true })
  }

  const total = requete.data?.total ?? 0
  const nbPages = Math.max(1, Math.ceil(total / TAILLE))

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Journal d&apos;audit</h1>
        <p className="text-sm text-slate-600">
          {total} entrées — toute lecture d&apos;une fiche patient y laisse une trace.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div>
          <label htmlFor="action" className="mb-1 block text-xs font-medium text-slate-600">Action</label>
          <Liste id="action" value={action} onChange={(e) => changer('action', e.target.value)}>
            <option value="">Toutes</option>
            {Object.keys(TONS).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Liste>
        </div>
        <div>
          <label htmlFor="entite" className="mb-1 block text-xs font-medium text-slate-600">Entité</label>
          <Liste id="entite" value={entite} onChange={(e) => changer('entite', e.target.value)}>
            <option value="">Toutes</option>
            <option value="patient">patient</option>
            <option value="intervention">intervention</option>
            <option value="utilisateur">utilisateur</option>
            <option value="releve_honoraires">releve_honoraires</option>
          </Liste>
        </div>
      </div>

      {requete.isPending ? (
        <SqueletteTableau colonnes={5} />
      ) : requete.isError ? (
        <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
      ) : total === 0 ? (
        <EtatVide icone="📋" titre="Aucune entrée" description="Aucune trace ne correspond à ces filtres." />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <caption className="sr-only">Journal d&apos;audit</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5">Horodatage</th>
                  <th scope="col" className="px-4 py-2.5">Action</th>
                  <th scope="col" className="px-4 py-2.5">Entité</th>
                  <th scope="col" className="px-4 py-2.5">Adresse IP</th>
                  <th scope="col" className="px-4 py-2.5">Détails</th>
                </tr>
              </thead>
              <tbody>
                {requete.data.items.map((entree) => {
                  const depliee = depliees.has(entree.id)
                  return (
                    <tr key={entree.id} className="border-b border-slate-100 align-top last:border-0">
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-slate-600">
                        {formatHorodatage(entree.created_at)}
                      </td>
                      <td className="px-4 py-2.5">
                        <Etiquette ton={TONS[entree.action] ?? 'neutre'}>{entree.action}</Etiquette>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">
                        {entree.entite}
                        {entree.entite_id ? (
                          <span className="block font-mono text-[10px] text-slate-400">
                            {entree.entite_id.slice(0, 13)}…
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-slate-600">{entree.adresse_ip ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        {entree.details ? (
                          <>
                            <button
                              type="button"
                              aria-expanded={depliee}
                              onClick={() =>
                                setDepliees((precedent) => {
                                  const suivant = new Set(precedent)
                                  if (suivant.has(entree.id)) suivant.delete(entree.id)
                                  else suivant.add(entree.id)
                                  return suivant
                                })
                              }
                              className="text-xs text-sky-700 hover:underline"
                            >
                              {depliee ? 'Masquer' : 'Afficher'}
                            </button>
                            {depliee ? (
                              <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 text-[11px] text-slate-700">
                                {JSON.stringify(entree.details, null, 2)}
                              </pre>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <nav className="flex items-center justify-between" aria-label="Pagination">
            <p className="text-sm text-slate-600">Page {page} sur {nbPages}</p>
            <div className="flex gap-2">
              <Bouton variante="secondaire" disabled={page <= 1} onClick={() => changer('page', String(page - 1))}>
                Précédente
              </Bouton>
              <Bouton variante="secondaire" disabled={page >= nbPages} onClick={() => changer('page', String(page + 1))}>
                Suivante
              </Bouton>
            </div>
          </nav>
        </>
      )}
    </div>
  )
}
